import { useEffect, useRef, useState } from "react";

const KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
let loaderPromise = null;

function ready() { return !!window.google?.maps?.Map; }

function loadGMaps() {
  if (ready()) return Promise.resolve();
  if (loaderPromise) return loaderPromise;
  loaderPromise = new Promise((resolve, reject) => {
    const waitReady = () => {
      let tries = 0;
      const iv = setInterval(() => {
        if (ready()) { clearInterval(iv); resolve(); }
        else if (++tries > 120) { clearInterval(iv); reject(new Error("gmaps timeout")); }
      }, 50);
    };
    const existing = document.querySelector("script[data-gmaps]");
    if (existing) { waitReady(); return; }
    const s = document.createElement("script");
    s.src = `https://maps.googleapis.com/maps/api/js?key=${KEY}`;
    s.async = true;
    s.defer = true;
    s.setAttribute("data-gmaps", "1");
    s.onload = waitReady;
    s.onerror = () => reject(new Error("gmaps load error"));
    document.head.appendChild(s);
  });
  return loaderPromise;
}

export function LiveTrackingMap({ pickup, delivery, trackers = [], height = 380, onRouteInfo }) {
  const ref = useRef(null);
  const mapRef = useRef(null);
  const objs = useRef({ markers: [], lines: [] });
  const fitRef = useRef(-1);
  const dirCache = useRef({});
  const dirSvcRef = useRef(null);
  const [err, setErr] = useState(!KEY);

  useEffect(() => {
    if (!KEY) { setErr(true); return; }
    let alive = true;
    loadGMaps().then(() => {
      if (!alive || !ref.current) return;
      try {
        if (!mapRef.current) {
          mapRef.current = new window.google.maps.Map(ref.current, {
            zoom: 5, center: { lat: 20.59, lng: 78.96 },
            mapTypeControl: false, streetViewControl: false, fullscreenControl: true,
          });
        }
        draw();
      } catch (e) { setErr(true); }
    }).catch(() => setErr(true));
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { if (mapRef.current && window.google?.maps) draw(); });

  function clear() {
    objs.current.markers.forEach((m) => m.setMap(null));
    objs.current.lines.forEach((l) => l.setMap(null));
    objs.current = { markers: [], lines: [] };
  }

  function draw() {
    const g = window.google.maps;
    const map = mapRef.current;
    clear();
    const bounds = new g.LatLngBounds();
    let count = 0;
    const add = (pos, label, color) => {
      const m = new g.Marker({
        position: pos, map,
        label: label ? { text: label, fontSize: "11px", fontWeight: "600", color: "#1A1A18" } : undefined,
        icon: { path: g.SymbolPath.CIRCLE, scale: 8, fillColor: color, fillOpacity: 1, strokeColor: "#fff", strokeWeight: 2 },
      });
      objs.current.markers.push(m);
      bounds.extend(pos);
      count += 1;
    };
    if (pickup) add({ lat: pickup.lat, lng: pickup.lng }, "Pickup", "#2F6F4E");
    if (delivery) add({ lat: delivery.lat, lng: delivery.lng }, "Delivery", "#8A2526");
    if (pickup && delivery) {
      objs.current.lines.push(new g.Polyline({ path: [pickup, delivery], map, strokeColor: "#C84B31", strokeOpacity: 0.5, strokeWeight: 2, geodesic: true }));
    }
    trackers.forEach((t) => {
      if (t.lat == null) return;
      add({ lat: t.lat, lng: t.lng }, t.label || "Driver", "#1A73E8");
      if (t.trail?.length > 1) {
        objs.current.lines.push(new g.Polyline({ path: t.trail.map((p) => ({ lat: p.lat, lng: p.lng })), map, strokeColor: "#1A73E8", strokeOpacity: 0.75, strokeWeight: 3 }));
        drawLastLegRoute(t);
      }
    });
    // Only auto-fit when the set of points changes (new pin joins), not on every
    // position tick — so live pins glide smoothly without the map re-zooming.
    if (count && fitRef.current !== count) {
      fitRef.current = count;
      if (count === 1) { map.setCenter(bounds.getCenter()); map.setZoom(13); }
      else { map.fitBounds(bounds, 60); }
    }
  }

  // Real driving route (orange) for the LATEST leg = between the last two pings.
  // Cached per (tracker, last-point) so a 5s poll with no movement won't re-hit Directions.
  function drawLastLegRoute(t) {
    const trail = t.trail || [];
    if (trail.length < 2) return;
    const a = trail[trail.length - 2];
    const b = trail[trail.length - 1];
    const key = `${t.tracker_id}:${a.at}:${b.at}`;
    const g = window.google.maps;
    if (dirCache.current[key]) {
      const c = dirCache.current[key];
      if (c.path) objs.current.lines.push(new g.Polyline({ path: c.path, map: mapRef.current, strokeColor: "#C84B31", strokeOpacity: 0.95, strokeWeight: 5 }));
      onRouteInfo?.(t.tracker_id, c.info);
      return;
    }
    if (!dirSvcRef.current) dirSvcRef.current = new g.DirectionsService();
    dirSvcRef.current.route(
      { origin: { lat: a.lat, lng: a.lng }, destination: { lat: b.lat, lng: b.lng }, travelMode: g.TravelMode.DRIVING },
      (res, status) => {
        if (status !== "OK" || !res.routes?.[0]) { dirCache.current[key] = { path: null, info: null }; return; }
        const leg = res.routes[0].legs?.[0] || {};
        const info = { distance_text: leg.distance?.text || "", distance_m: leg.distance?.value || 0, duration_text: leg.duration?.text || "" };
        const path = res.routes[0].overview_path;
        dirCache.current[key] = { path, info };
        objs.current.lines.push(new g.Polyline({ path, map: mapRef.current, strokeColor: "#C84B31", strokeOpacity: 0.95, strokeWeight: 5 }));
        onRouteInfo?.(t.tracker_id, info);
      },
    );
  }

  if (err) return <div className="text-sm text-[#8A2526] border border-dashed border-[#E5E3DB] p-4 rounded" data-testid="live-map-unavailable">Map unavailable — Google Maps key missing or invalid.</div>;
  return <div ref={ref} style={{ height, width: "100%" }} className="border border-[#E5E3DB] rounded" data-testid="live-tracking-map" />;
}

export default LiveTrackingMap;
